import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, computed, runInAction, reaction, makeObservable } from "mobx";
import { RcFile, UploadFile, UploadFileStatus, UploadProps } from "antd/lib/upload/interface";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore, userInfoStore } from "Stores";
import { API } from "General/API";
import { getUid } from "../../../../utils/uid";
import Axios, { AxiosProgressEvent } from "axios";
import { FileUploadChunked } from "./FileUploadChunked";
import { mergeSignals } from "utils/mergeSignals";
import { messageInstance } from "App";
import i18n, { I18N_NS } from "Config/i18n";

export interface UploadedTmpFile extends UploadFile, Omit<API_UploadFile, "size" | "name" | "type"> {
  id?: number; // id of a file in temp database
  abortController?: AbortController;
  preparedAlready?: boolean;
}

export type UploadMode = API_MODULEDATA_MODULE;

export interface InitModalOpts {
  folderId?: number;
  file?: API_FileAttachment & Pick<API_UploadFile, "mailId">;
  module?: UploadMode;
  mailId?: string;
  mailType?: API_MAILS_TYPE;
  mailProjectSpecific?: boolean;
  workflowId?: number;
  workflowActionId?: number;
  workflowAccess?: API_FILELISTREST_ACCESS;
  fileNameRulesEnabled?: boolean;
  metadataEnabled?: boolean;
  // list of files uploaded to workflow that are used to be replaced by newly uploaded file
  // should be converted to API_FileDataEntity to have same schema for ReplaceFileStore.ts
  workflowFileList?: API_FileInfo[];
  onSuccess?: () => void;
}

interface FileQueueItem {
  uid: string;
  name: string;
  file: File;
  size?: number;
  type?: string;
}

interface SpecificWorkflowOptions {
  workflowActionId?: number;
  workflowId?: number;
  workflowAccess?: API_FILELISTREST_ACCESS;
  fileNameRulesEnabled?: boolean;
  metadataEnabled?: boolean;
  onSuccess?: () => void; // function to be called after successful file upload to update some table data.
}

interface SpecificCommunicationOptions {
  file?: API_FileAttachment & Pick<API_UploadFile, "mailId">;
  mailId?: string;
  mailType?: API_MAILS_TYPE;
  mailProjectSpecific?: boolean;
  onSuccess?: () => void; // function to be called after successful file upload to update some table data.
}

export const UPLOAD_FAILED = "UPLOAD_FAILED";

export class FileUploadStore extends CoreStore {
  tempFilesMap: Map<number | string, UploadedTmpFile> = new Map();
  // Map of files that are uploaded to temp db
  filesMap: Map<number | string, UploadedTmpFile> = new Map();
  isVisible = false;
  folderId: number; // Folder Id where to upload the files
  folderName: string; // Folder name upload the files
  replaceFiles = false; // Whether replacement of files is approved
  /* list of tmpFileId's (files that are uploaded to temp db) that
   * have conflicts with existing files in project */
  result: API_UploadData = {}; // preserved post object for uploading files

  activeSessions = 0; // amount of pending upload requests
  filesQuery: FileQueueItem[] = []; // buffer to store all Files somewhere;
  specificWorkflowOptions: SpecificWorkflowOptions = {};
  specificCommunicationOptions: SpecificCommunicationOptions = {};
  module: UploadMode;
  WFfilesToReplace: API_FileDataEntity[] = [];
  directoriesCreated: boolean;
  zipProgress: number;
  private readonly fileNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  private readonly compareFilesByNameAsc = (a: UploadedTmpFile, b: UploadedTmpFile): number => {
    const compareByName = this.fileNameCollator.compare(a.name ?? "", b.name ?? "");
    if (compareByName !== 0) {
      return compareByName;
    }

    // Keep sorting stable when names are identical to avoid UI row jumping.
    return this.fileNameCollator.compare(String(a.uid ?? a.id ?? ""), String(b.uid ?? b.id ?? ""));
  };

  uploadNextFile() {
    this.prepareTempFiles(this.filesQuery[0]);
    this.filesQuery.shift();
  }

  constructor() {
    super();

    makeObservable(this, {
      tempFilesMap: observable.shallow,
      filesMap: observable.shallow,
      isVisible: observable,
      folderId: observable,
      folderName: observable,
      replaceFiles: observable,
      result: observable.shallow,
      activeSessions: observable,
      filesQuery: observable,
      specificWorkflowOptions: observable,
      specificCommunicationOptions: observable,
      module: observable,
      WFfilesToReplace: observable,
      directoriesCreated: observable,
      initModal: action.bound,
      hideModal: action.bound,
      setFolderId: action.bound,
      clear: action.bound,
      onRequest: action.bound,
      zipExtract: action.bound,
      uploadNextFile: action.bound,
      setFilesToUpload: action.bound,
      handleUploadProgress: action.bound,
      handleSuccess: action.bound,
      prepareTempFiles: action.bound,
      toggleReplaceFiles: action.bound,
      editFile: action.bound,
      fileUploadNotFinished: computed,
      filesList: computed,
      uploadFilesList: computed,
      fileIndexIsRequired: computed,
      indexCanBeEdited: computed,
      mergeExternalFiles: action.bound,
      setWorkflowFilesToReplace: action.bound,
      getFilesByErrorStatus: action.bound,
      fetchFolderById: action.bound,
      checkUpload: action.bound,
      uploadFiles: action.bound,
      deleteFile: action.bound,
      backToUploadStep: action.bound,
      zipProgress: observable,
      setZipProgress: action.bound,
    });
  }

  async initModal(opts: InitModalOpts): Promise<void> {
    const options = { ...opts };
    console.log(opts);
    if (!options.module) {
      options.module = "DOCUMENTS";
    }
    if (options.module === "DOCUMENTS") {
      if (!options.folderId) {
        throw Error('"folderId" is required when use fileUploadStore inside "DOCUMENTS".');
      }
      try {
        await this.fetchFolderById(options.folderId);
      } catch (error) {
        throw error;
      }
    }
    runInAction(() => {
      this.isVisible = true;
      this.folderId = options.folderId;
      this.zipProgress = 0;
      this.module = options.module || "DOCUMENTS";
      if (options.module === "WORKFLOW") {
        this.specificWorkflowOptions.workflowId = options.workflowId;
        this.specificWorkflowOptions.workflowActionId = options.workflowActionId;
        this.specificWorkflowOptions.fileNameRulesEnabled = options.fileNameRulesEnabled;
        this.specificWorkflowOptions.metadataEnabled = options.metadataEnabled;
        this.specificWorkflowOptions.workflowAccess = options.workflowAccess;
        this.specificWorkflowOptions.onSuccess = options.onSuccess;
        this.folderId = -1;
        this.setWorkflowFilesToReplace(options.workflowFileList, options.workflowAccess);
      } else if (options.module === "VIEW") {
        this.folderId = -109;
      } else if (options.module === "MAIL") {
        this.specificCommunicationOptions.onSuccess = options.onSuccess;
        this.specificCommunicationOptions.mailId = options.mailId;
        this.specificCommunicationOptions.mailProjectSpecific = options.mailProjectSpecific;
        this.specificCommunicationOptions.mailType = options.mailType;
        this.specificCommunicationOptions.file = { ...options.file, mailId: options.file.mailId };
      }
      // if (!this.isVisible) {
      //   this.clear();
      // }
    });

    return Promise.resolve();
  }

  hideModal() {
    this.isVisible = false;
    this.reset();
  }

  setFolderId(id: number) {
    this.folderId = id;
  }

  clear() {
    this.tempFilesMap = new Map();
    this.filesMap = new Map();
    this.folderName = undefined;
    this.folderId = undefined;
    this.isVisible = false;
    this.replaceFiles = false;
    this.directoriesCreated = false;
    this.replaceFiles = false;
    this.result = {};
    this.filesQuery = [];
    this.specificWorkflowOptions = {};
    this.module = "DOCUMENTS";
    this.WFfilesToReplace = [];
    this.zipProgress = 0;
  }

  getFileById = (id: string | number) => {
    return this.filesMap.get(id);
  };

  url() {
    return API_URL.FILES.persistFiles(projectsDataStore.projectId, this.folderId);
  }

  prepareUploadRequestBody(
    extraOptions: Record<string, unknown> = {},
    ignoreAlreadyPrepared?: boolean,
    isZipFile: API_UploadFile = null
  ) {
    const body: API_UploadData = {
      checkIndex: true,
      workflowActionId: this.specificWorkflowOptions.workflowActionId,
      workflowId: this.specificWorkflowOptions.workflowId,
    };
    if (this.replaceFiles) {
      body.fileReplace = true;
    }
    if (projectsDataStore.functions.includes("AUTOMATICDIRMAPPING")) {
      body.fileAutoMapping = true;
    }
    if (!isZipFile) {
      body.files = this.filesList.reduce((buffer, item) => {
        if (!ignoreAlreadyPrepared || !item.preparedAlready) {
          buffer.push({
            description: item.description,
            descriptionAlreadySet: item.descriptionAlreadySet,
            dirid: item.dirid,
            dirname: item.dirname,
            oldFile: item.oldFile,
            id: item.id,
            index: item.index,
            name: item.name,
            size: item.size,
            metadata: item.metadata,
            type: item.type,
            oldMailAttachmentFileId: item.oldMailAttachmentFileId,
            ...extraOptions,
          });
        }
        return buffer;
      }, []);
    } else {
      body.files = [isZipFile];
    }
    return body;
  }

  @handleStoreState()
  async fetchFolderById(folderId: number) {
    try {
      // tslint:disable-next-line:max-line-length
      const response = await API.getData(API_URL.FOLDERS.getSubfolders(projectsDataStore.projectId, folderId));
      runInAction(() => {
        this.folderName = response.name;
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * To validate file upload on back-end without file submitting
   */
  @handleStoreState()
  async checkUpload() {
    const body = this.prepareUploadRequestBody();
    console.log("body", body);
    const params = this.module === "WORKFLOW" ? { module: "WORKFLOW" } : null;

    try {
      const response: API_UploadData = await API.newData(
        API_URL.FILES.checkFiles(projectsDataStore.projectId, this.folderId),
        body,
        params
      );
      runInAction(() => {
        console.log("response", response.files.slice());
        // set updated values from back-end
        response.files.forEach((item) => {
          const oldFile = this.filesMap.get(item.id);
          if (oldFile) {
            this.filesMap.set(item.id, {
              ...oldFile,
              ...item,
            });
          }
        });
        this.result = response;
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * To move files in project
   */
  @handleStoreState()
  async moveFiles() {
    try {
      const { mailId, mailProjectSpecific, mailType } = this.specificCommunicationOptions;
      const body = this.prepareUploadRequestBody({ mailId });
      const response: API_UploadData = await API.newData(
        API_URL.MAILS.moveAttachment(projectsDataStore.projectId, mailType, mailId, this.folderId),
        body,
        { project: mailProjectSpecific }
      );
      runInAction(() => {
        this.result = response;
        if (!response.uploadSuccess) {
          throw UPLOAD_FAILED;
        }
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * To submit files into project
   */
  @handleStoreState()
  async uploadFiles() {
    const body = this.prepareUploadRequestBody();
    const params = this.module === "WORKFLOW" ? { module: "WORKFLOW" } : null;

    try {
      const response: API_UploadData = await API.newData(this.url(), body, params);
      runInAction(() => {
        this.result = response;
        if (!response.uploadSuccess) {
          throw UPLOAD_FAILED;
        }
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * For custom request handling.
   * Check https://github.com/react-component/upload#customrequest for more info
   */
  onRequest({ file }: Parameters<UploadProps<File>["customRequest"]>[0]): UploadProps["customRequest"] {
    const id = getUid();
    this.filesQuery.push({
      uid: id,
      name: (file as File).name,
      file: file as File,
    });
    this.filesMap.set(id, {
      uid: id,
      originFileObj: {} as RcFile,
      name: (file as File).name,
      size: (file as File).size,
      type: (file as File).type,
    });
    this.tempFilesMap.set(id, {
      uid: id,
      originFileObj: {} as RcFile,
      name: (file as File).name,
      size: (file as File).size,
      type: (file as File).type,
    });
    return undefined;
  }

  setFilesToUpload(files: File[]) {
    const filesInfo = files.map((item) => ({
      uid: getUid(),
      name: item.name,
      file: item,
      size: item.size,
      type: item.type,
    }));

    filesInfo.forEach((item) => {
      this.filesMap.set(item.uid, {
        uid: item.uid,
        originFileObj: {} as RcFile,
        name: item.name,
        size: item.size,
        type: item.type,
      });
      this.tempFilesMap.set(item.uid, {
        uid: item.uid,
        originFileObj: {} as RcFile,
        name: item.name,
        size: item.size,
        type: item.type,
      });
    });
    this.filesQuery = filesInfo.slice();
  }

  async checkMailAttachs(init: boolean = false) {
    try {
      const { mailId, mailProjectSpecific, mailType, file } = this.specificCommunicationOptions;
      let body: API_UploadData;
      if (init) {
        body = { files: [{ dirid: this.folderId, id: file.id, name: file.name, mailId }] };
      } else {
        body = this.prepareUploadRequestBody({ mailId });
      }
      const response: API_UploadData = await API.newData(
        API_URL.MAILS.checkAttachment(projectsDataStore.projectId, mailType, mailId, this.folderId),
        body,
        { project: mailProjectSpecific }
      );
      if (response && response.files) {
        const responseFile = response.files[0];
        this.filesMap.set(response.files[0].id, {
          uid: `${response.files[0].id}`,
          originFileObj: {} as RcFile,
          id: responseFile.id,
          percent: 100,
          size: responseFile.size,
          name: responseFile.name,
          type: responseFile.type.toLowerCase(),
          ...responseFile,
        });
        this.specificCommunicationOptions.file = this.filesMap.get(responseFile.id);
        this.result = response;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async prepareTempFiles({ uid, name, file }: FileQueueItem) {
    const cancelController = new AbortController();
    const abortSignal = mergeSignals(cancelController.signal, this.abortController.signal);

    try {
      const fileStatus: UploadedTmpFile = {
        uid,
        originFileObj: {} as RcFile,
        name,
        status: "uploading",
        size: file.size,
        type: file.type,
        abortController: cancelController,
      };
      this.filesMap.set(uid, fileStatus);
      this.tempFilesMap.set(uid, fileStatus);

      let folderId: number = this.folderId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let params: any = null;
      if (this.module === "WORKFLOW") {
        folderId = -1;
        params = { module: "WORKFLOW" };
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("filename", name); // Because back-end can't parse umlauts from file.
      if (this.specificWorkflowOptions.workflowActionId) {
        formData.append("workflowActionId", this.specificWorkflowOptions.workflowActionId.toString());
      }
      if (this.specificWorkflowOptions.workflowId) {
        formData.append("workflowId", this.specificWorkflowOptions.workflowId.toString());
      }
      this.activeSessions += 1;
      if (file.size > FileUploadChunked.UPLOAD_CONSTANSTS?.triggerChunkuploadMB * 1000 * 1000) {
        console.log("FileUploadChunked");

        await new FileUploadChunked(uid, file, abortSignal, {
          fileName: name,
          dirId: folderId,
          module: this.module,
          workflowActionId: this.specificWorkflowOptions.workflowActionId,
          workflowId: this.specificWorkflowOptions.workflowId,
        }).init(params);
      } else {
        const response = await API.newData<API_UploadData>(
          API_URL.FILES.prepareTempFiles(projectsDataStore.projectId, folderId),
          formData,
          params,
          {
            onUploadProgress: (progress: AxiosProgressEvent) => this.handleUploadProgress(uid, progress),
            signal: abortSignal,
          },
          true
        );
        this.handleSuccess(uid, response.files);
      }
    } catch (error) {
      if (Axios.isCancel(error)) {
        console.warn("request cancelled", error.message);
      } else {
        if (error.message === "Error NETWORK: Request failed") {
          messageInstance.error(
            i18n.t(
              // eslint-disable-next-line max-len
              "Cannot access file. It may be locked by another application, or you have problems with your network connection.",
              { ns: I18N_NS.COMPONENT_UPLOAD }
            )
          );
          this.filesMap.delete(uid);
          this.tempFilesMap.delete(uid);
        }

        throw error;
      }
    } finally {
      runInAction(() => {
        this.activeSessions -= 1;
      });
    }
  }

  /**
   * before this was introduced, on BE the directory-mapping etc. was proccesed directly,
   * after uploading a single file. Now we split the first step into two:
   * 1. prepareTempFiles which only uploads the file to temp (one request per file)
   * and
   * 2. uploadFilesToTempDirectory which then does the mapping for all files (one request for all files)
   * look at PAVE-574 for details
   */
  async uploadFilesToTempDirectory() {
    const body = this.prepareUploadRequestBody(undefined, true);
    const params = this.module === "WORKFLOW" ? { module: "WORKFLOW" } : null;

    // all files should only run one time to uploadFilesToTempDirectory method
    if (body.files.length === 0) {
      // if for all files the uploadFilesToTempDirectory was called already
      // It the case, when the user hit back in sec. step and then doesn't add futher files
      return;
    }

    try {
      const response: API_UploadData = await API.newData(
        API_URL.FILES.checkFiles(projectsDataStore.projectId, this.folderId),
        body,
        params
      );
      runInAction(() => {
        // set updated values from back-end
        response.files.forEach((item) => {
          const oldFile = this.filesMap.get(item.id);
          if (oldFile) {
            this.filesMap.set(item.id, {
              ...oldFile,
              ...item,
              preparedAlready: true,
            });
            // this.tempFilesMap.set(item.id, {
            //   ...oldFile,
            //   ...item,
            //   preparedAlready: true,
            // });
          }
        });
        this.result = response;
      });
    } catch (error) {
      throw error;
    }
  }

  handleUploadProgress(id: string, { loaded, total }: AxiosProgressEvent) {
    this.filesMap.set(id, {
      ...this.filesMap.get(id),
      status: "uploading",
      percent: Math.round((loaded / total) * 100),
    });
    // this.tempFilesMap.set(id, {
    //   ...this.tempFilesMap.get(id),
    //   status: "uploading",
    //   percent: Math.round((loaded / total) * 100),
    // });
  }

  handleSuccess(customId: string, files: API_UploadFile[]) {
    files.forEach((responseFile) => {
      // set upload result from unpacked zip-archive in case response object has multiple files
      if (files.length > 1) {
        this.filesMap.set(responseFile.id, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          uid: responseFile.id as any,
          originFileObj: {} as RcFile,
          status: "done" as UploadFileStatus,
          id: responseFile.id,
          percent: 100,
          size: responseFile.size,
          name: responseFile.name,
          type: responseFile.type.toLowerCase(),
          ...responseFile,
        });
        this.filesMap.delete(customId); // remove archive from attaches list
        this.tempFilesMap.set(responseFile.id, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          uid: responseFile.id as any,
          originFileObj: {} as RcFile,
          status: "done" as UploadFileStatus,
          id: responseFile.id,
          percent: 100,
          size: responseFile.size,
          name: responseFile.name,
          type: responseFile.type.toLowerCase(),
          ...responseFile,
        });
        this.tempFilesMap.delete(customId); // remove archive from attaches list
      } else if (files.length === 1) {
        // otherwise just update existing object
        this.filesMap.set(files[0].id, {
          uid: customId,
          originFileObj: {} as RcFile,
          status: "done" as UploadFileStatus,
          id: responseFile.id,
          percent: 100,
          size: responseFile.size,
          name: responseFile.name,
          type: responseFile.type.toLowerCase(),
          ...responseFile,
        });
        this.filesMap.delete(customId);
        this.tempFilesMap.set(files[0].id, {
          uid: customId,
          originFileObj: {} as RcFile,
          status: "done" as UploadFileStatus,
          id: responseFile.id,
          percent: 100,
          size: responseFile.size,
          name: responseFile.name,
          type: responseFile.type.toLowerCase(),
          ...responseFile,
        });
        this.tempFilesMap.delete(customId);
      } else {
        throw new Error('received empty array in "files"');
      }
    });
  }

  toggleReplaceFiles() {
    this.replaceFiles = !this.replaceFiles;
  }

  @handleStoreState()
  deleteFile(id: number | string) {
    const file = this.filesMap.get(id);
    const tfile = this.tempFilesMap.get(id);
    if (file?.abortController) {
      file.abortController.abort();
    }
    if (tfile?.abortController) {
      tfile.abortController.abort();
    }
    this.filesMap.delete(id);
    this.tempFilesMap.delete(id);
    if (this.result.files) {
      this.result.files = this.result.files.filter((item) => item.id !== id);
    }
  }

  deleteFileByFilename(name: string) {
    const entry = Array.from(this.tempFilesMap.entries()).find(([id, file]) => {
      return file.name === name;
    });
    if (entry?.[0]) {
      this.deleteFile(entry?.[0]);
    }
  }

  async cleanTempFolder() {
    await API.deleteData(API_URL.FILES.cleanupTmpDirV2(projectsDataStore.projectId));
  }

  setZipProgress(percent: number) {
    this.zipProgress = percent;
  }

  async zipExtract(extractMode: API_ARCHIVEDOBJECT_EXTRACTMODE, zipFileId: number) {
    const ignoreZipOriginalFile = extractMode !== "NONE";
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { uid, originFileObj, status, percent, ...file } = this.filesMap.get(zipFileId);
    const zipFile: API_UploadFile = { ...file, extractMode, ignore: ignoreZipOriginalFile };
    const body = this.prepareUploadRequestBody(null, false, zipFile);
    console.log(body);
    try {
      const response = await API.newData<API_UploadData>(
        API_URL.FILES.extractZip(projectsDataStore.projectId, this.folderId),
        body,
        null,
        {
          onProgress: fileUploadStore.setZipProgress,
        }
      );
      if (response) {
        runInAction(() => {
          this.directoriesCreated = response.directoriesCreated;

          ignoreZipOriginalFile && this.filesMap.delete(zipFileId);
          response.files.forEach((item) => {
            if (item.id !== zipFileId) {
              const itemWithStatus = {
                ...item,
                status: "done",
                percent: 100,
              };
              const oldFile = this.filesMap.get(item.id);
              if (oldFile) {
                this.filesMap.set(item.id, {
                  ...oldFile,
                  ...itemWithStatus,
                } as UploadedTmpFile);
              } else {
                this.filesMap.set(item.id, {
                  uid: `${item.id}`,
                  ...itemWithStatus,
                } as UploadedTmpFile);
              }
            }
          });
        });
      }
    } catch (error) {
      if (Axios.isCancel(error)) {
        console.warn("request cancelled", error.message);
      } else {
        throw error;
      }
    }
  }

  editFile(file: API_UploadFile) {
    const oldValues = this.filesMap.get(file.id);
    const newValues = { ...oldValues, ...file };
    if (oldValues.name !== newValues.name || oldValues.dirid !== newValues.dirid) {
      // see PAVE-901
      newValues.oldFile = undefined;
      file.oldFile = undefined;
    }
    this.filesMap.set(file.id, newValues);
    if (this.result.files) {
      const idx = this.result.files.findIndex((item) => item.id === file.id);
      if (idx !== -1) {
        this.result.files[idx] = newValues;
      }
    }
  }

  get fileUploadNotFinished() {
    return this.filesList.some((file) => file.status !== "done");
  }

  get filesList() {
    return Array.from(this.filesMap.values()).sort(this.compareFilesByNameAsc);
  }

  get uploadFilesList() {
    return Array.from(this.tempFilesMap.values()).sort(this.compareFilesByNameAsc);
  }

  get fileIndexIsRequired() {
    return !projectsDataStore.currentProject.functions.includes("FILENAMECHECK");
  }

  get indexCanBeEdited() {
    if (projectsDataStore.currentProject.autoIndex) {
      return false;
    }
    return true;
    // // if index field for some of the files is empty
    // return this.result && this.result.files.some(item => !item.index);
  }

  mergeExternalFiles = (file: API_UploadFile) => {
    this.filesMap.set(file.id, {
      ...file,
      id: file.id,
      originFileObj: {} as RcFile,
      uid: file.id.toString(),
      name: file.name,
      size: file.size,
      type: file.type,
      status: "done",
      percent: 100,
    });
  };

  setWorkflowFilesToReplace(workflowFiles: API_FileInfo[], workflowAccess: API_FILELISTREST_ACCESS) {
    const mapProps = (file: API_FileInfo): API_FileDataEntity => ({
      id: file.id,
      description: file.description,
      index: file.index,
      created: file.fileLastChange.toString(),
      malware: file.malware,
      name: file.fileName,
      ownerFirstName: file.fileOwnerFirstName,
      ownerSurname: file.fileOwnerSurname,
      type: file.fileType,
      state: file.fileState,
    });
    // files replacement option is allowed only if user has some any of WRITE access rights
    if (workflowAccess === "WRITE") {
      this.WFfilesToReplace = workflowFiles?.map(mapProps);
    } else if (workflowAccess === "WRITEOWN") {
      const userId = userInfoStore.id;
      this.WFfilesToReplace = workflowFiles.reduce((arr, item) => {
        if (item.ownerId === userId) {
          arr.push(mapProps(item));
        }
        return arr;
      }, []);
    }
  }

  getFilesByErrorStatus(status: API_UPLOADERRORINFO_STATUS) {
    const errorFiles: UploadedTmpFile[] = [];
    this.filesMap.forEach((file) => {
      if (file.errors?.status === status) {
        errorFiles.push(file);
      }
    });
    return errorFiles;
  }

  backToUploadStep() {
    this.filesMap.clear();
    this.tempFilesMap.forEach((v, k) => {
      this.filesMap.set(k, Object.assign({}, v));
    });
  }
}

export const fileUploadStore = new FileUploadStore();

/**
 * This section controls file upload process to handle maximum 5 active requests.
 * https://mobx.js.org/refguide/reaction.html
 */
reaction(
  () => ({
    count: fileUploadStore.activeSessions,
    query: fileUploadStore.filesQuery.length,
  }),
  ({ count, query }) => {
    if (count < 5 && query > 0) {
      fileUploadStore.uploadNextFile();
    }
  }
);

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).fileUpload = fileUploadStore;
}
